import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input } from '@accure/ui';
import { Portal } from '@radix-ui/react-portal';
import { LoaderCircle } from 'lucide-react';

import { cls } from '@/utils/cls';
import { plural, singular } from '@/utils/format/label';

export type ComboboxOption = { value: string; label?: string };

export type ComboboxProps = {
  options: ComboboxOption[] | undefined;
  onSelect?: (value: string) => void;
  className?: string;
  labels?: [string, string];
  isLoading?: boolean;
};

/**
 * To-do: Replace with a shadcn implementation in React 18
 */
export function Combobox({
  options = [],
  onSelect,
  className,
  labels = ['option', 'options'],
  isLoading = false,
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [filteredOptions, setFilteredOptions] =
    useState<ComboboxOption[]>(options);
  const [showOptions, setShowOptions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (showOptions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 1000,
      });
    }
  }, [showOptions]);

  useEffect(() => {
    setFilteredOptions(
      options.filter((option) =>
        (option.label ?? option.value)
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    );
  }, [options, query]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setShowOptions(true);
  };

  const handleSelect = (option: ComboboxOption) => {
    setQuery(option.label ?? option.value);
    setShowOptions(false);
    onSelect?.(option.value);
  };

  return (
    <div className={cls('relative w-64', className)}>
      <Input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleChange}
        placeholder={`Select ${singular(labels)}...`}
        onFocus={() => setShowOptions(true)}
        onBlur={() => setTimeout(() => setShowOptions(false), 200)}
      />
      {isLoading && showOptions ? (
        <Portal>
          <div
            style={dropdownStyle}
            className="max-h-60 overflow-auto rounded-xl border border-gray-300 bg-white shadow-lg"
          >
            <div className="flex items-center gap-2 p-4 text-neutral-400">
              <LoaderCircle className="size-6 animate-spin text-neutral-300 [animation-duration:2s]" />
              <span className="inline-flex animate-pulse">
                Loading {plural(labels)}...
              </span>
            </div>
          </div>
        </Portal>
      ) : null}
      {!isLoading && showOptions && (
        <Portal>
          <ul
            style={dropdownStyle}
            className="pointer-events-auto max-h-60 overflow-auto rounded-xl border border-gray-300 bg-white shadow-lg"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  className="m-2 cursor-pointer rounded p-2 hover:bg-primary-50 hover:text-primary-800"
                >
                  {opt.label ?? opt.value}
                </li>
              ))
            ) : (
              <li className="p-2 text-gray-500">No {plural(labels)} found</li>
            )}
          </ul>
        </Portal>
      )}
    </div>
  );
}
